import React, { useState, useEffect, memo } from "react";
import { useAuth } from "react-oidc-context";
import {
    ContextualMenu,
    IColumn,
    IconButton,
    IDropdownOption,
    MessageBarType,
    Stack,
    StackItem,
    Spinner, Icon, TooltipOverflowMode
} from "@fluentui/react";
import { POIItem } from "../../../model/ManagePOIListItem";
import Filter from "./FilterPOIList";
import { fetchPOI, fetchPOIById, checkPOIAssessmentsById } from "../../../services/ManageSystemServices/POIService";
import AddEditDialog from "../Dialogs/AddEditPOIDialog";
import Pagination from "../../Pagination/Pagination";
import EiopaFnPIcons from "../../EiopaFnPIcons/EiopaFnPIcons";
import styles from "./POIList.module.css";
import strings from "../../../loc/strings";
import { poiListLegalColumns, poiListNaturalColumns } from "./POIListUtils";
import { LEGAL_PERSON_TYPE, NATURAL_PERSON_TYPE } from "../../../constants/EiopaFnPConstants";
import { displayMessageBar } from "../../LayoutWithAuth/LayoutWithAuth";
import SearchForm from "../../SearchForm/SearchForm";
import PersonOfInterestDetailsList from "../../PersonOfInterestDetailsList/PersonOfInterestDetailsList";
import { SearchResultItem } from "../../../model/SearchResultItem";
import { searchCriteriaForm, SearchError } from "../../../services/SearchServices/SearchService";
import { SearchCriteria } from "../../../model/SearchCriteria";
import { FnPUser } from "../../../model/FnPUser";
import renderItemColumn from "../../PersonOfInterestDetailsList/PersonOfInterestDetailsList";
import { _legalColumns, _naturalColumns } from "../../SearchForPOI/SearchForPOIUtils";
import { LS_USER_INFO } from "../../../constants/EiopaFnPConstants";
import { IContextualMenuProps } from "@fluentui/react/lib/ContextualMenu";
import { TooltipHost } from "@fluentui/react/lib/Tooltip";
import { TypeOption } from "../../../model/TypeOption";
import { fetchAssessmentStatus } from "../../../services/AssessmentServices/AssessmentService";
import { initializeIcons } from "@fluentui/font-icons-mdl2";
import { TextBulletList20Regular, FluentIconsProps } from "@fluentui/react-icons";
import ServiceEndpoints from "../../../services/ServiceEndpoints";

initializeIcons();

// pagination interface 
interface IPagination {
    searchResult: SearchResultItem;
    totalItems: number;
    totalPages: number;
    modalAction: string;
    isExactOnly: boolean;
    isAkaEnabled: boolean;
    loading: boolean;
    isDetailsListVisible: boolean;
    isResultsFetched: boolean;
    selectedKey: string;
    clickedPersonId: number;
    EncryptedId: string;
    name: string;
    searchCriteria: SearchCriteria;
    initialSearchCrit: SearchCriteria;
    columnsState: IColumn[];
    currentPage: number;
    itemsPerPage: number;
    isActionsCalloutVisible: boolean;
    errorMessage: string | null;
}

function POIList() {

    const auth = useAuth();
    let userInfo: FnPUser = { username: "", ncaCode: "", roles: [] };
    const userInfoLS: string = localStorage.getItem(LS_USER_INFO) as string;

    if (userInfoLS !== null) {
        userInfo = JSON.parse(userInfoLS);
    }

    const personTypeOptions: IDropdownOption[] = [
        { key: NATURAL_PERSON_TYPE, text: "Natural" },
        { key: LEGAL_PERSON_TYPE, text: "Legal" },
    ];

    const typeOptions: TypeOption[] = [
        { key: "1", text: "Natural" },
        { key: "2", text: "Legal" },
    ];

    const [selectedPersonType, setSelectedPersonType] = useState<number>(personTypeOptions[0].key as number);
    const [selectedPersonTypeInDialog, setSelectedPersonTypeInDialog] = useState(NATURAL_PERSON_TYPE);
    const [DOBirth, setDOBirth] = useState<Date | undefined>();
    const [dialogIsHidden, setDialogIsHidden] = React.useState(true);
    const [items, setItems] = React.useState<POIItem[]>([]);
    const [itemsOrg, setItemsOrg] = React.useState<POIItem[]>([]);
    const [editItem, setEditItem] = React.useState<POIItem>(Object);
    const [dateOfBirth, setDateOfBirth] = useState<Date | undefined>(undefined);
    const [isDateValid, setIsDateValid] = useState(true);
    const [dateString, setDateString] = useState<string | undefined>(undefined);
    const [hasErrorMessage, setHasErrorMessage] = useState(false);
    const initialSelectedKey = typeOptions[0].key;
    const [resetFlag, setResetFlag] = useState(false);
    const [isHistoricalPeriodEnded, setEndOfHistoricalPeriod] = useState<boolean>(false);
    const [state, setState] = React.useState<IPagination>({
        searchResult: {
            results: [
                {
                    PersonId: 0,
                    FirstName: "",
                    LastName: "",
                    LegalName: "",
                    CountryInc: "",
                    LeiRegistration: "",
                    CityOfBirth: "",
                    CountryOfBirth: "",
                    PersonType: "",
                    AlsoKnownAs: "",
                    DateOfBirth: "",
                    SocialSecurityNumber: "",
                    ID: "",
                    CAInternalAssessmentNo: "",
                    personOfInterestId: 0,
                    IsExactMatch: false,
                    searchScore: 0,
                    hasAssesmentForNca: false
                },
            ],
            totalItems: 0,
            totalPages: 0,
        },
        isActionsCalloutVisible: false,
        modalAction: "",
        isExactOnly: false,
        isAkaEnabled: true,
        loading: false,
        isDetailsListVisible: false,
        isResultsFetched: false,
        selectedKey: initialSelectedKey,
        clickedPersonId: 0,
        EncryptedId: "",
        name: "",
        currentPage: 1,
        itemsPerPage: 10,
        searchCriteria: {
            firstName: "",
            lastName: "",
            legalName: "",
            countryInc: "",
            leiRegistration: "",
            cityOfBirth: "",
            countryOfBirth: "",
            personType: "Natural",
            alsoKnownAs: "",
            dateOfBirth: "",
            socialSecurityNumber: "",
            idNumber: "",
        },
        initialSearchCrit: {
            firstName: "",
            lastName: "",
            legalName: "",
            countryInc: "",
            leiRegistration: "",
            cityOfBirth: "",
            countryOfBirth: "",
            personType: "",
            alsoKnownAs: "",
            dateOfBirth: "",
            socialSecurityNumber: "",
            idNumber: "",
        },
        errorMessage: null,
    } as IPagination);

    const _columns = state.selectedKey === typeOptions[0].key ? _naturalColumns : _legalColumns;

    const handleReset = () => {
        setResetFlag(!resetFlag);
    };

    const handleOpenDialog = (personType: number) => {
        setSelectedPersonTypeInDialog(personType);
        setDialogIsHidden(false);
    };

    const parseDate = (dateOfBirth?: string): Date | undefined => {
        if (dateOfBirth) {
            if (dateOfBirth !== null && dateOfBirth !== '') {
                const [day, month, year] = dateOfBirth.split('/').map(Number);
                return new Date(year, month - 1, day);
            }
            return undefined;
        }
        return undefined;
    };

    useEffect(() => {
        (async function() {
            try {
                const response = await fetch(ServiceEndpoints.GetEndOfHistoricalPeriod, {
                    headers: {
                        Authorization: `Bearer ${auth.user?.id_token}`,
                    },
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch end of historical period");
                }

                const result = await response.json();
                setEndOfHistoricalPeriod(result.endOfHistoricalPeriod);

            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    // Function to handle page change pagination
    const _onPageChange = async (selectedPage: number) => {
        setState((prevState) => ({
            ...prevState,
            loading: true,
            errorMessage: null,
        }));
        // Clear any existing error message
        displayMessageBar(MessageBarType.info, "", false);

        try {
            const data = await searchCriteriaForm(
                _handleSearch(),
                selectedPage,
                state.itemsPerPage,
                state.isExactOnly,
                state.isAkaEnabled,
                auth.user?.id_token
            );

            // Check if the response is an error
            if ('errors' in data) {
                // Handle validation errors
                const errorMessages = data.errors?.join(', ') || 'An error occurred during search';
                setState((prevState) => ({ 
                    ...prevState, 
                    errorMessage: errorMessages,
                    loading: false,
                    isResultsFetched: false,
                    isDetailsListVisible: false
                }));
                // Display error message
                displayMessageBar(MessageBarType.error, errorMessages, false);
            } else {
                setState((prevState) => {
                    return { 
                        ...prevState, 
                        searchResult: data as SearchResultItem, 
                        currentPage: selectedPage, 
                        loading: false,
                        errorMessage: null
                    };
                });
            }
        } catch (error) {
            // Handle unexpected errors
            const errorMessage = 'An unexpected error occurred during search';
            setState((prevState) => ({ 
                ...prevState, 
                errorMessage: errorMessage,
                loading: false,
                isResultsFetched: false,
                isDetailsListVisible: false
            }));
            // Display error message
            displayMessageBar(MessageBarType.error, errorMessage, false);
        }
    };

    // Function to handle itemsPerPage change pagination
    const _onChangeItemsPerPage = async (
        event: React.FormEvent<HTMLDivElement>,
        item: IDropdownOption
    ) => {

        setState((prevState) => ({
            ...prevState,
            loading: true,
            errorMessage: null,
        }));
        // Clear any existing error message
        displayMessageBar(MessageBarType.info, "", false);

        try {
            const data = await searchCriteriaForm(
                _handleSearch(),
                1,
                item.key,
                state.isExactOnly,
                state.isAkaEnabled,
                auth.user?.id_token
            );

            // Check if the response is an error
            if ('errors' in data) {
                // Handle validation errors
                const errorMessages = data.errors?.join(', ') || 'An error occurred during search';
                setState((prevState) => ({ 
                    ...prevState, 
                    errorMessage: errorMessages,
                    loading: false,
                    isResultsFetched: false,
                    isDetailsListVisible: false
                }));
                // Display error message
                displayMessageBar(MessageBarType.error, errorMessages, false);
            } else {
                setState((prevState) => ({
                    ...prevState,
                    searchResult: data as SearchResultItem, 
                    itemsPerPage: item.key as number, 
                    currentPage: 1, 
                    loading: false,
                    errorMessage: null
                }));
            }
        } catch (error) {
            // Handle unexpected errors
            const errorMessage = 'An unexpected error occurred during search';
            setState((prevState) => ({ 
                ...prevState, 
                errorMessage: errorMessage,
                loading: false,
                isResultsFetched: false,
                isDetailsListVisible: false
            }));
            // Display error message
            displayMessageBar(MessageBarType.error, errorMessage, false);
        }
    };

    function GetMenu(props: IContextualMenuProps): JSX.Element {
        const [loading, isLoading] = useState(true);
        useEffect(() => {
            (function () {
                fetchAssessmentStatus(props.id ?? "", auth.user?.id_token).then((resp) => {
                    props.items.forEach(item => {
                        if (item.key === "edit") {
                            item.disabled = !resp.hasAssessmentsForNca
                        }
                    })
                    isLoading(false)
                }).catch();
            })();
            return () => { };
        }, []);
        return (
            loading ? <Spinner size={0} style={{ padding: "10px" }} /> : <ContextualMenu {...props} />
        );
    }

    const handleActionResponse = (response: string, eiopaErrorMessage?: string) => {
        switch (response) {
            case "poicreatetrue":
                displayMessageBar(MessageBarType.success, 'The POI is added successfully in the system.', false);
                break;
            case "poicreatefalse":
                displayMessageBar(MessageBarType.error, 'Error the POI already exists.', false);
                break;
            case "poicreateexist":
                displayMessageBar(MessageBarType.error, 'The specified POI already exists. To add new assessments, locate the POI and click Edit/View Assessments.', false);
                break;
            case "poiedittrue":
                displayMessageBar(MessageBarType.success, 'The POI is updated successfully.', false);
                break;
            case "poieditfalse":
                displayMessageBar(MessageBarType.error, 'Error the POI could not be updated.', false);
                break;
            case "poideletefalse":
                displayMessageBar(MessageBarType.error, 'Error the POI could not be deleted from the system.', false);
                break;
            case "poideletetrue":
                displayMessageBar(MessageBarType.success, 'The POI is deleted successfully from the system.', false);
                break;
            case "poieditexist":
                displayMessageBar(MessageBarType.error, 'The edited POI already exists in the system', false);
                break;
            case "bulkcreatetrue":
                displayMessageBar(MessageBarType.success, 'All the records of the CSV file were successfully imported into the system.', false);
                break;
            case "bulkcreatefalse":
                displayMessageBar(MessageBarType.error, 'The CSV file failed to be uploaded.', false);
                break;
            case "bulkcreateerrors":
                displayMessageBar(MessageBarType.success, 'The uploaded CSV file contains records that were not successfully imported, since certain validation rules were not met', false);
                break;
            case "error":
                // Display the specific error message from the API
                displayMessageBar(MessageBarType.error, eiopaErrorMessage ?? strings.assessmentDefaultErrorMessage, false);
                break;
            default:
                displayMessageBar(MessageBarType.error, eiopaErrorMessage ?? response, false);
                break;
        }
    }

    const _handleDropDownChange = (event: any, item: any) => {
        setState((prevState) => {
            if (item.key !== prevState.selectedKey) {
                return {
                    ...prevState,
                    selectedKey: item.key,
                    searchCriteria: {
                        ...prevState.initialSearchCrit,
                        personType: item.text,
                    },
                    isDetailsListVisible: false,
                    currentPage: 1,
                    modalAction: item.text,
                };
            }
            return prevState;
        });
    };

    const _handleSearchItem = (e: any) => {
        const { name, value } = e.target;
        setState((prevState) => {
            const newState = {
                ...prevState,
                searchCriteria: {
                    ...prevState.searchCriteria,
                    [name]: value,
                },
            };
            return newState;
        });
    };

    function formatDateToYYYYMMDD(date?: Date) {
        if (date === undefined || date === null) {
            return '';
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        const formattedDate = `${year}-${month}-${day}`;
        return formattedDate;
    }

    const _handleDateChange = (date: any, name: any) => {
        setDateOfBirth(date)
        setState((prevState) => {
            const updatedState = {
                ...prevState,
                searchCriteria: {
                    ...prevState.searchCriteria,
                    [name]: formatDateToYYYYMMDD(date),
                },
            };
            return updatedState;
        });
    };

    function _isExactMatch() {
        setState((prevState) => ({
            ...prevState,
            isExactOnly: !state.isExactOnly
        }))
    };

    function _isAkaEnabled() {
        setState((prevState) => ({
            ...prevState,
            isAkaEnabled: !state.isAkaEnabled
        }))
    }

    const _handleSearch = () => {
        const searchCritFormFields: SearchCriteria = {
            firstName: state.searchCriteria.firstName,
            lastName: state.searchCriteria.lastName,
            legalName: state.searchCriteria.legalName,
            countryInc: state.searchCriteria.countryInc,
            leiRegistration: state.searchCriteria.leiRegistration,
            cityOfBirth: state.searchCriteria.cityOfBirth,
            countryOfBirth: state.searchCriteria.countryOfBirth,
            personType: state.searchCriteria.personType,
            alsoKnownAs: state.searchCriteria.alsoKnownAs,
            dateOfBirth: state.searchCriteria.dateOfBirth as unknown as Date,
            socialSecurityNumber: state.searchCriteria.socialSecurityNumber,
            idNumber: state.searchCriteria.idNumber,
        };

        return searchCritFormFields;
    };

    const _handleSearchClick = async () => {
        try {
            // Set loading to true and maintain the previous state
            setState((prevState) => ({ ...prevState, loading: true, currentPage: 1, errorMessage: null }));
            // Clear any existing error message
            displayMessageBar(MessageBarType.info, "", false);

            const data = await searchCriteriaForm(
                _handleSearch(),
                1,
                state.itemsPerPage,
                state.isExactOnly,
                state.isAkaEnabled,
                auth.user?.id_token
            );

            // Check if the response is an error
            if ('errors' in data) {
                // Handle validation errors
                const errorMessages = data.errors?.join(', ') || 'An error occurred during search';
                setState((prevState) => ({ 
                    ...prevState, 
                    errorMessage: errorMessages,
                    isResultsFetched: false,
                    isDetailsListVisible: false,
                    loading: false
                }));
                // Display error message
                displayMessageBar(MessageBarType.error, errorMessages, false);
            } else {
                // Update the state based on the previous state
                setState((prevState) => ({ 
                    ...prevState, 
                    searchResult: data as SearchResultItem,
                    errorMessage: null,
                    loading: false,
                    isResultsFetched: true,
                    isDetailsListVisible: true
                }));
            }
        } catch (error) {
            // Handle unexpected errors
            const errorMessage = 'An unexpected error occurred during search';
            setState((prevState) => ({ 
                ...prevState, 
                errorMessage: errorMessage,
                isResultsFetched: false,
                isDetailsListVisible: false,
                loading: false
            }));
            // Display error message
            displayMessageBar(MessageBarType.error, errorMessage, false);
        }
    };

    const _onSearchClick = () => {
        _handleSearchClick();
        // The isResultsFetched and isDetailsListVisible flags will be set in _handleSearchClick
        // based on whether there's an error or not
        setState((prevState) => ({
            ...prevState,
            currentPage: 1,
        }));
    };

    const _onReset = () => {
        handleReset();
        setState({
            ...state,
            searchCriteria: state.initialSearchCrit,
            isResultsFetched: false,
            isExactOnly: false,
            isAkaEnabled: true,
            currentPage: 1,
            errorMessage: null,
        });
        setDateOfBirth(undefined)
        setIsDateValid(true)
        // Clear any displayed error message
        displayMessageBar(MessageBarType.info, "", false);
    };

    const isDisabled = () => {
        if (state.selectedKey === typeOptions[0].key) {
            if (
                (state.searchCriteria.firstName.trim() !== "" &&
                    state.searchCriteria.lastName.trim() !== "" &&
                    state.searchCriteria.dateOfBirth && isDateValid && dateOfBirth) ||
                (state.searchCriteria.idNumber.trim() !== "" ||
                state.searchCriteria.socialSecurityNumber.trim() !== "") && !hasErrorMessage
            ) {
                return false;
            }
        } else if (state.selectedKey === typeOptions[1].key) {
            if (
                state.searchCriteria.legalName.trim() !== ""
            ) {
                return false;
            }
        }

        return true;
    };

    const isGroupFlowOneRequired = () => {
        return (
            (state.searchCriteria.idNumber === "" &&
                state.searchCriteria.socialSecurityNumber === "") &&
            (state.searchCriteria.firstName !== "" ||
                state.searchCriteria.lastName !== "" ||
                state.searchCriteria.countryOfBirth !== "" ||
                state.searchCriteria.cityOfBirth !== "" ||
                state.searchCriteria.dateOfBirth !== "")
        );
    };

    const isGroupFlowTwoRequired = () => {
        return (
            state.searchCriteria.legalName !== ""
        );
    };

    const checkcAssessments = async (personEncryptedId: string) => {
        return await checkPOIAssessmentsById(personEncryptedId, auth.user?.id_token);
    };

    function formatDateTime(inputDateString: any) {
        if (inputDateString === null || inputDateString === undefined || inputDateString === "") {
            return '';
        }

        const inputDate = new Date(inputDateString);

        const day = String(inputDate.getDate()).padStart(2, '0');
        const month = String(inputDate.getMonth() + 1).padStart(2, '0'); // Months are zero-based
        const year = inputDate.getFullYear();

        const formattedDate = `${day}/${month}/${year}`;

        return formattedDate;
    }

    const _items = () => {
        return state.selectedKey === typeOptions[0].key
            ? state.searchResult.results.map((item) => ({
                FirstName: item.FirstName,
                LastName: item.LastName,
                AlsoKnownAs: item.AlsoKnownAs,
                DateOfBirth: formatDateTime(item.DateOfBirth),
                ID: item.ID,
                PersonType: !item.PersonType ? typeOptions[0].text : item.PersonType,
                SocialSecurityNumber: item.SocialSecurityNumber,
                RelevanceScore: item.searchScore,
                IsExactMatch: item.IsExactMatch,
                placeOfBirth: (item.CityOfBirth && item.CountryOfBirth) ? `${item.CityOfBirth}, ${item.CountryOfBirth}` : '',
                personId: item.PersonId,
                encryptedId: item.EncryptedId,
                hasAssesmentForNca: item.hasAssesmentForNca,
            }))
            : state.selectedKey === typeOptions[1].key
                ? state.searchResult.results.map((item) => ({
                    LegalName: item.LegalName,
                    AlsoKnownAs: item.AlsoKnownAs,
                    LeiRegistration: item.LeiRegistration,
                    CountryInc: item.CountryInc,
                    RelevanceScore: item.searchScore,
                    PersonType: !item.PersonType ? typeOptions[1].text : item.PersonType,
                    IsExactMatch: item.IsExactMatch,
                    personId: item.PersonId,
                    encryptedId: item.EncryptedId,
                    hasAssesmentForNca: item.hasAssesmentForNca,
                }))
                : state.searchResult.results;
    };

    const handleItemClick = (personEncryptedId: string) => {
        (async function () {
            const _itemsPOI = await fetchPOIById(personEncryptedId, auth.user?.id_token);
            if (_itemsPOI.length > 0 || _itemsPOI.length != undefined) {
                setEditItem(_itemsPOI[0]);
                setDOBirth(parseDate(_itemsPOI[0].dateOfBirth ?? ""));
                handleOpenDialog(_itemsPOI[0].personTypeID);
            }
        })();
    };

    function _onRenderSettingsIcon() {
        return <EiopaFnPIcons iconName={"actions"} iconColor={"#0078D4"} />;
    }

    function _onRenderActionsMenuIcon() {
        return <></>;
    }

    const iconStyleProps: FluentIconsProps = {
        primaryFill: "#0078D4",
        className: styles.filterIcon,
    };

    function onRenderPrefix() {
        return <TextBulletList20Regular aria-label={"Filter"} {...iconStyleProps} />;
    }

    // Function to render columns
    const renderEditItemColumn = (
        item: any,
        index: number | undefined,
        column: IColumn | undefined
    ): React.ReactNode => {

        if (item && column) {
            const fieldContent = item[column.fieldName as string] as string;

            if (column.fieldName === "RelevanceScore") {

                if (item.IsExactMatch) {
                    return (
                        <span>
                            <Icon iconName="Flag" style={{ color: "green", fontSize: 20 }} />
                        </span>
                    );
                }
                return item.RelevanceScore;
            } else
                if (column.fieldName === "Actions") {

                    const menuProps: IContextualMenuProps = {
                        items: [
                            {
                                key: "edit",
                                text: "Edit POI details",
                                iconProps: { iconName: 'EditSolid12' },
                                onClick: () =>
                                    handleItemClick(
                                        item.encryptedId,
                                    ),
                            },
                            {
                                key: "view",
                                text: "View / Edit assessments",
                                onRenderIcon: onRenderPrefix,
                                href: '/manage-system-data/poi-list/assessments/' + item.encryptedId,
                                target: '_blank',
                                rel: 'opener',
                            },
                        ],
                        directionalHintFixed: false,
                        isBeakVisible: false,
                        id: item.encryptedId,
                    };
                    return (
                        <IconButton
                            menuProps={menuProps}
                            menuAs={GetMenu}
                            onRenderIcon={_onRenderSettingsIcon}
                            onRenderMenuIcon={_onRenderActionsMenuIcon}
                            title="Settings"
                            ariaLabel="Emoji"
                        />
                    );
                }
                else {
                    return <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={fieldContent}>{fieldContent}</TooltipHost>;
                }
        }
    };

    return (
        <Stack>
            <StackItem className={styles.PoiListHeader}>
                {"POI list"}
            </StackItem>
            <SearchForm
                typeOptions={typeOptions}
                _handleDropDownChange={_handleDropDownChange}
                selectedKey={state.selectedKey}
                _handleSearchItem={_handleSearchItem}
                searchCriteria={state.searchCriteria}
                _handleDateChange={_handleDateChange}
                _isExactMatch={_isExactMatch}
                isExactOnly={state.isExactOnly}
                _isAkaEnabled={_isAkaEnabled}
                isAkaEnabled={state.isAkaEnabled}
                _onSearchClick={_onSearchClick}
                _onReset={_onReset}
                isDisabled={isDisabled}
                isGroupNaturalRequired={isGroupFlowOneRequired}
                isGroupLegalRequired={isGroupFlowTwoRequired}
                // errorMessage={getErrorMessageDateNew}
                dateOfBirth={dateOfBirth}
                setDateString={setDateString}
                dateString={dateString}
                hasErrorMessage={setHasErrorMessage}
                resetFlag={resetFlag}
            />
            <StackItem>
                <Filter handleActionResponse={handleActionResponse}
                    personTypeOptions={personTypeOptions}
                    selectedPersonType={selectedPersonType}
                    isHistoricalPeriodEnded = {isHistoricalPeriodEnded}
                />
            </StackItem>
            {state.loading ? (
                <Spinner label={strings.spinnerLabel} />
            ) : state.isResultsFetched && state.isDetailsListVisible ? (
                <>
                    {(_items().length === 0 && state.isExactOnly) ||
                        _items().length === 0 ? (
                        <Stack>
                            {strings.noResultsText}
                        </Stack>
                    ) : (
                        <Stack>
                            <StackItem>
                                <PersonOfInterestDetailsList
                                    userInfo={userInfo}
                                    items={_items()}
                                    renderItemColumn={renderEditItemColumn}
                                    type={state.selectedKey}
                                    columns={_columns}
                                    handleItemClick={handleItemClick}
                                />
                            </StackItem>
                            <StackItem>
                                <Pagination
                                    currentPage={state.currentPage}
                                    onPageChange={_onPageChange}
                                    itemsPerPage={state.itemsPerPage}
                                    onChangeItemsPerPage={_onChangeItemsPerPage}
                                    totalItems={state.searchResult.totalItems}
                                />
                            </StackItem>
                        </Stack>
                    )}
                </>
            ) : null}
            <AddEditDialog
                item={editItem}
                dialogIsHidden={dialogIsHidden}
                setDialogIsHidden={setDialogIsHidden}
                title="Edit POI details"
                dateOfBirth={DOBirth}
                handleActionResponse={handleActionResponse}
                selectedPersonTypeInDialog={selectedPersonTypeInDialog}
                setSelectedPersonTypeInDialog={setSelectedPersonTypeInDialog}
                isHistoricalPeriodEnded = {isHistoricalPeriodEnded}
            />
        </Stack>
    );
}

export default POIList;
